import React, {
  useEffect,
  useState,
  useCallback,
  Suspense,
  useMemo,
} from "react";
import {
  useParams,
  useNavigate,
  useLocation,
  useOutletContext,
} from "react-router-dom";
import { apiClient } from "services/Api";
import {
  CCard,
  CCardFooter,
  CFormInput,
  CCardBody,
  CButton,
  CRow,
  CCol,
  CFormSelect,
  CDropdown,
  CDropdownToggle,
  CDropdownMenu,
  CSpinner,
  CBadge,
} from "@coreui/react";
import {
  cilChevronLeft,
  cilChevronRight,
  cilArrowLeft,
  cilSave,
  cilFolderOpen,
  cilCloudDownload,
  cilX,
} from "@coreui/icons";
import CIcon from "@coreui/icons-react";

import CompanyLogo from "components/CompanyLogo";
import { triggerAlert } from "components/globals/AlertNotification";
import ToastNotification from "components/globals/ToastNotification";
import LoadingScreen from "components/globals/LoadingScreen";
import SubmitApplication from "../_components/SubmitApplication";
import { reloadWithSectionQueryParam } from "../_utils/queryParams";
import {
  requestApplicationPdfExport,
  triggerDownloadFromUrl,
  waitForApplicationPdfExport,
} from "../_utils/applicationPdfExport";
import {
  sections as sections2025_7,
  importFiles as files2025_7,
} from "./mpr_2025_7/_index";
import {
  sections as sections2025_9,
  importFiles as files2025_9,
} from "./mpr_2025_9/_index";
import {
  sections as sections2026_6,
  importFiles as files2026_6,
} from "./mpr_2026_6/_index";
import {
  sections as sections2026_10,
  importFiles as files2026_10,
} from "./mpr_2026_10/_index";
import {
  sections as sectionsCompete2030_2026_12,
  importFiles as filesCompete2030_2026_12,
} from "./compete2030_2026_12/_index";
import {
  sections as sections06_C05_i14_01_2026,
  importFiles as files06_C05_i14_01_2026,
} from "./06_C05-i14.01_2026/_index";
import ExcelUploadButton from "../_components/ExcelUploadButton";
import ImportPreviewModal from "../_components/ImportPreviewModal";
import StreamPilotDrawer from "./06_C05-i14.01_2026/_components/StreamPilotDrawer";
import DraftRecovery from "../_components/DraftRecovery";

const RD_SECTION_IDS = new Set([
  "projectFrameworkResearchAndDevelopment",
  "investmentsResearchAndDevelopment",
]);
const PRODUCTIVE_INNOVATION_SECTION_IDS = new Set([
  "projectFrameworkProductiveInnovation",
  "investmentsProductiveInnovation",
]);

const StreamPilotRobotIcon = () => (
  <svg
    viewBox="0 0 24 24"
    width="20"
    height="20"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d="M12 3v3" />
    <path d="M8.5 3h7" />
    <rect x="5" y="7" width="14" height="10" rx="3" />
    <path d="M9 17v3" />
    <path d="M15 17v3" />
    <path d="M5 12H3" />
    <path d="M21 12h-2" />
    <circle cx="9.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
    <circle cx="14.5" cy="11.5" r="1" fill="currentColor" stroke="none" />
    <path d="M9 14h6" />
  </svg>
);
const cloneContent = (value) => {
  if (typeof structuredClone === "function") {
    return structuredClone(value);
  }

  return JSON.parse(JSON.stringify(value ?? {}));
};

const areValuesEqual = (left, right) => {
  if (left === right) return true;

  if (left && right && typeof left === "object" && typeof right === "object") {
    return JSON.stringify(left) === JSON.stringify(right);
  }

  return false;
};

const InovApplication = () => {
  const { id } = useParams();

  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const [application, setApplication] = useState(null);
  const [formData, setFormData] = useState(null);
  const [jsonData, setJsonData] = useState(null);
  const [sections, setSections] = useState(null);
  const [importFiles, setImportFiles] = useState(null);
  const [currentSectionId, setCurrentSectionId] = useState(null);
  const [currentSubsection, setCurrentSubsection] = useState(null);

  const [loadingPdf, setLoadingPdf] = useState(false);
  const [streamPilotVisible, setStreamPilotVisible] = useState(false);
  const [importPreview, setImportPreview] = useState(null);
  const exportPollRef = React.useRef(new Set());

  const { setSidebar } = useOutletContext();
  const [toast, setToast] = useState();

  const draftRef = React.useRef(false);

  const loadApplication = useCallback(async () => {
    const response = await apiClient.get(`/application/innovation/${id}/`);
    const applicationContent = response.data.application.content ?? {};

    setApplication({ ...response.data.application });
    setFormData(cloneContent(applicationContent));
    setJsonData(response.data.jsonData);
  }, [id]);

  useEffect(() => {
    loadApplication().catch((error) =>
      console.error("Erro ao carregar candidatura:", error),
    );
  }, [loadApplication]);

  useEffect(() => {
    const exportTaskId = application?.export_task_id;
    if (!exportTaskId || exportPollRef.current.has(exportTaskId)) return;

    exportPollRef.current.add(exportTaskId);
    setLoadingPdf(true);

    waitForApplicationPdfExport({
      typology: "innovation",
      applicationId: id,
      taskId: exportTaskId,
    })
      .then(async (result) => {
        await triggerDownloadFromUrl(result.downloadUrl, result.filename);
        setToast({
          title: "Pdf",
          message: `PDF ${application?.client_name || "Candidatura"} gerado com sucesso!`,
          type: "success",
        });
      })
      .catch(() =>
        setToast({
          title: "Pdf",
          message: `Ocorreu um erro ao gerar o PDF ${application?.client_name || "Candidatura"}!`,
          type: "error",
        }),
      )
      .finally(async () => {
        exportPollRef.current.delete(exportTaskId);
        setLoadingPdf(false);
        setApplication((current) =>
          current ? { ...current, export_task_id: null } : current,
        );
      });
  }, [
    application?.client_name,
    application?.export_task_id,
    id,
    loadApplication,
  ]);

  useEffect(() => {
    if (application?.notice) {
      if (application.notice === "MPr-2025-7") {
        setSections(sections2025_7);
        setImportFiles(files2025_7);
      } else if (application.notice === "MPr-2025-9") {
        setSections(sections2025_9);
        setImportFiles(files2025_9);
      } else if (application.notice === "MPr-2026-6") {
        setSections(sections2026_6);
        setImportFiles(files2026_6);
      } else if (application.notice === "MPr-2026-10") {
        setSections(sections2026_10);
        setImportFiles(files2026_10);
      } else if (application.notice === "COMPETE2030-2026-12") {
        setSections(sectionsCompete2030_2026_12);
        setImportFiles(filesCompete2030_2026_12);
      } else if (application.notice === "06/C05-i14.01/2026") {
        setSections(sections06_C05_i14_01_2026);
        setImportFiles(files06_C05_i14_01_2026);
      }
    }
  }, [application]);

  const visibleSections = useMemo(() => {
    if (!sections) return null;
    if (application?.notice !== "06/C05-i14.01/2026") return sections;

    const hasResearchAndDevelopment =
      formData?.projectTypeResearchAndDevelopment ?? true;
    const hasProductiveInnovation =
      formData?.projectTypeProductiveInnovation ?? true;

    return sections.filter((section) => {
      if (RD_SECTION_IDS.has(section.id)) {
        return hasResearchAndDevelopment;
      }

      if (PRODUCTIVE_INNOVATION_SECTION_IDS.has(section.id)) {
        return hasProductiveInnovation;
      }

      return true;
    });
  }, [
    sections,
    application?.notice,
    formData?.projectTypeResearchAndDevelopment,
    formData?.projectTypeProductiveInnovation,
  ]);

  useEffect(() => {
    if (visibleSections?.length) {
      const requestedSectionId = queryParams.get("seccao");
      const nextSectionId = visibleSections.some(
        (section) => section.id === currentSectionId,
      )
        ? currentSectionId
        : visibleSections.some((section) => section.id === requestedSectionId)
          ? requestedSectionId
          : visibleSections[0].id;

      if (nextSectionId !== currentSectionId) {
        setCurrentSectionId(nextSectionId);
      }
    }
  }, [visibleSections, currentSectionId, location.search]);

  useEffect(() => {
    if (!queryParams.toString()) {
      window.history.pushState(
        { url: window.location.href },
        document.title,
        window.location.href,
      );
    }

    window.addEventListener("beforeunload", handleBeforeUnload);
    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  useEffect(() => {
    setSidebar(false, null);
  }, [setSidebar]);

  const handleBeforeUnload = useCallback((event) => {
    event.preventDefault();
    event.returnValue = "";
  }, []);

  const handlePopState = useCallback((event) => {
    event.preventDefault();
    const confirmLeave = window.confirm(
      "You have unsaved changes. Do you really want to leave?",
    );
    if (confirmLeave) {
      navigate(-1);
    } else {
      window.history.pushState(null, document.title, window.location.href);
    }
  }, []);

  const handleChange = (fieldId, value) => {
    setFormData((prevFormData) => {
      const newFormData = { ...prevFormData };
      const keys = Array.isArray(fieldId) ? fieldId : [fieldId];
      let current = newFormData;
      keys.forEach((key, index) => {
        if (index === keys.length - 1) {
          current[key] = value;
        } else {
          current = current[key] = { ...current[key] };
        }
      });
      return newFormData;
    });
  };

  const handleImportFile = (fcn) => (workbook) => {
    const stagedChanges = [];
    let stagedToast = null;

    const stageChange = (fieldId, value) => {
      stagedChanges.push({ fieldId, value });
    };
    const stageToast = (toastPayload) => {
      stagedToast = toastPayload;
    };

    fcn(workbook, formData, jsonData, stageChange, stageToast);

    if (stagedChanges.length === 0) {
      if (stagedToast) setToast(stagedToast);
      return;
    }

    setImportPreview({ changes: stagedChanges, toast: stagedToast });
  };

  const handleConfirmImport = (selectedChanges) => {
    selectedChanges.forEach(({ fieldId, value }) => handleChange(fieldId, value));
    if (importPreview?.toast) setToast(importPreview.toast);
    setImportPreview(null);
  };

  const handleCancelImport = () => setImportPreview(null);

  const hydratedRef = React.useRef(false);
  useEffect(() => {
    if (!application || !formData) return;
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      return;
    }
    draftRef.current?.scheduleCurrSave?.(1000);
  }, [formData, application]);

  const handleSave = () => {
    const changedFields = Object.keys(formData).reduce((acc, key) => {
      if (!areValuesEqual(formData[key], application.content[key])) {
        acc[key] = formData[key];
      }
      return acc;
    }, {});

    apiClient
      .put(`/application/innovation/${id}/`, { content: changedFields })
      .then(() => {
        window.removeEventListener("beforeunload", handleBeforeUnload);

        triggerAlert("Candidatura guardada com sucesso!", "success");
        reloadWithSectionQueryParam(currentSectionId);

        draftRef.current?.clearAllDrafts?.();
      })
      .catch((error) =>
        setToast({ title: "Candidatura", message: error, type: "error" }),
      );
  };

  const handleNext = () => {
    const currentIndex = visibleSections.findIndex(
      (s) => s.id === currentSectionId,
    );
    if (currentIndex < visibleSections.length - 1) {
      setCurrentSectionId(visibleSections[currentIndex + 1].id);
    }
  };

  const handlePrevious = () => {
    const currentIndex = visibleSections.findIndex(
      (s) => s.id === currentSectionId,
    );
    if (currentIndex > 0) {
      setCurrentSectionId(visibleSections[currentIndex - 1].id);
    }
  };

  const downloadPDF = async () => {
    if (loadingPdf || !id) return;
    const clientName = application?.client_name || "Candidatura";

    setLoadingPdf(true);
    try {
      const startResult = await requestApplicationPdfExport({
        typology: "innovation",
        applicationId: id,
      });

      if (startResult.status === "SUCCESS" && startResult.downloadUrl) {
        await triggerDownloadFromUrl(
          startResult.downloadUrl,
          startResult.filename,
        );
        setToast({
          title: "Pdf",
          message: `PDF ${clientName} gerado com sucesso!`,
          type: "success",
        });
        setApplication((current) =>
          current ? { ...current, export_task_id: null } : current,
        );
        return;
      }

      if (startResult.status === "PENDING" && startResult.taskId) {
        const result = await waitForApplicationPdfExport({
          typology: "innovation",
          applicationId: id,
          taskId: startResult.taskId,
        });
        await triggerDownloadFromUrl(result.downloadUrl, result.filename);
        setToast({
          title: "Pdf",
          message: `PDF ${clientName} gerado com sucesso!`,
          type: "success",
        });
        setApplication((current) =>
          current ? { ...current, export_task_id: null } : current,
        );
      }
    } catch (error) {
      setToast({
        title: "Pdf",
        message: `Ocorreu um erro ao gerar o PDF ${clientName}!`,
        type: "error",
      });
    } finally {
      setLoadingPdf(false);
    }
  };

  if (!application || !visibleSections || !currentSectionId)
    return <LoadingScreen message="A carregar candidatura..." />;

  const currentSection = visibleSections.find((s) => s.id === currentSectionId);
  if (!currentSection) {
    return <LoadingScreen message="A carregar candidatura..." />;
  }
  const CurrentComponent = currentSection.component;
  const hasStreamPilotDrawer = application?.notice === "06/C05-i14.01/2026";

  return (
    <div id="application">
      {/* Toolbar */}
      <CCard
        className="d-flex flex-row justify-content-between align-items-center px-3 py-3 mb-3"
        style={{ position: "sticky", top: 65, zIndex: 950 }}
      >
        <div className="d-flex gap-2">
          <CButton
            color="secondary"
            className="d-flex align-items-center"
            onClick={() => navigate("/candidaturas/inovacao")}
          >
            <CIcon icon={cilArrowLeft} className="me-2" size="lg" />
            Voltar
          </CButton>

          <CDropdown>
            <CDropdownToggle color="info" className="d-flex align-items-center">
              <CIcon icon={cilFolderOpen} className="me-2" size="lg" />
              Carregar Dados
            </CDropdownToggle>
            <CDropdownMenu>
              {importFiles &&
                Object.entries(importFiles)?.map(([label, fcn]) => (
                  <ExcelUploadButton
                    label={label}
                    onFileLoaded={handleImportFile(fcn)}
                    onException={setToast}
                  />
                ))}
              {!importFiles && (
                <small className="px-3 text-muted">Não Disponível</small>
              )}
            </CDropdownMenu>
          </CDropdown>

          <CButton
            color="success"
            className="d-flex align-items-center"
            onClick={downloadPDF}
            disabled={loadingPdf}
          >
            {loadingPdf ? (
              <CSpinner size="sm" className="me-2" />
            ) : (
              <CIcon icon={cilCloudDownload} className="me-2" size="lg" />
            )}
            Exportar
          </CButton>
        </div>

        <div className="d-flex gap-2">
          <DraftRecovery
            ref={draftRef}
            appId={id}
            notice={application?.notice}
            schemaVersion="v1"
            baseGetter={() => application?.content ?? null}
            currentGetter={() => formData ?? null}
            setFormData={setFormData}
            setToast={setToast}
          />

          <SubmitApplication
            id={id}
            typology="innovation"
            taskId={application.task_id}
            submission={application.submission}
            setToast={setToast}
            pages={visibleSections.map((section) => section.title)}
          />

          <CButton
            color="success"
            className="d-flex align-items-center"
            onClick={handleSave}
          >
            <CIcon icon={cilSave} className="me-2" size="lg" />
            Guardar
          </CButton>
        </div>
      </CCard>

      <ToastNotification toast={toast} setToast={setToast} />

      <ImportPreviewModal
        visible={!!importPreview}
        changes={importPreview?.changes ?? []}
        onCancel={handleCancelImport}
        onConfirm={handleConfirmImport}
      />

      {/* Header */}
      <CCard className="mb-3">
        <CCardBody className="px-5 py-4">
          <CRow className="align-items-center">
            <CCol className="text-center mr-5t" md={6}>
              {application.client_image ? (
                <CompanyLogo
                  logo={application.client_image}
                  maxWidth={200}
                  maxHeight={160}
                />
              ) : (
                <h3 className="mb-0 fw-bold">{application.client_name}</h3>
              )}
            </CCol>
            <CCol md={3}>
              <CRow className="mb-2">
                <CCol>
                  <strong>Tipologia:</strong>
                  <CFormInput value="Inovação" type="text" disabled readOnly />
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <strong>Aviso:</strong>
                  <CFormInput
                    value={application.notice}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
            </CCol>
            <CCol md={3}>
              <CRow className="mb-2">
                <CCol>
                  <strong>Criado:</strong>
                  <CFormInput
                    value={formatDate(application.created_at)}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
              <CRow>
                <CCol>
                  <strong>Ultima Atualização:</strong>
                  <CFormInput
                    value={formatDate(application.updated_at)}
                    type="text"
                    disabled
                    readOnly
                  />
                </CCol>
              </CRow>
            </CCol>
          </CRow>
        </CCardBody>
        <CCardFooter className="px-5">
          <CRow className="justify-content-between">
            <CCol md={12} className="d-flex justify-content-between">
              <CButton
                className="d-flex align-items-center me-2"
                color="secondary"
                onClick={handlePrevious}
                disabled={currentSectionId === visibleSections[0].id}
              >
                <CIcon icon={cilChevronLeft} className="me-2" />
                <small>Anterior</small>
              </CButton>

              <CFormSelect
                value={currentSectionId}
                className="text-center fw-bold"
                onChange={(e) => setCurrentSectionId(e.target.value)}
              >
                {visibleSections.map((section) => (
                  <option key={section.id} value={section.id}>
                    {section.title}
                  </option>
                ))}
              </CFormSelect>

              <CButton
                className="d-flex align-items-center ms-2"
                color="primary"
                onClick={handleNext}
                disabled={
                  currentSectionId ===
                  visibleSections[visibleSections.length - 1].id
                }
              >
                <small className="me-2">Seguinte</small>
                <CIcon icon={cilChevronRight} />
              </CButton>
            </CCol>
          </CRow>
        </CCardFooter>
      </CCard>

      <CCard className="mb-3">
        <CCardBody className="px-5 py-5">
          <Suspense
            fallback={
              <div className="pt-3 text-center">
                <CSpinner
                  color="primary"
                  style={{ width: "4rem", height: "4rem" }}
                />
              </div>
            }
          >
            <CurrentComponent
              formData={formData}
              handleChange={handleChange}
              setFormData={setFormData}
              setToast={setToast}
              setCurrentSubsection={setCurrentSubsection}
              jsonData={jsonData}
            />
          </Suspense>
        </CCardBody>
      </CCard>

      {hasStreamPilotDrawer && (
        <CButton
          className={`stream-pilot-fab ${streamPilotVisible ? "is-open" : ""}`}
          onClick={() => setStreamPilotVisible((current) => !current)}
        >
          <span className="stream-pilot-fab__icon" aria-hidden="true">
            {streamPilotVisible ? (
              <CIcon icon={cilX} size="lg" />
            ) : (
              <StreamPilotRobotIcon />
            )}
          </span>
          <span className="stream-pilot-fab__label">Stream/ Pilot</span>
        </CButton>
      )}

      {hasStreamPilotDrawer && (
        <StreamPilotDrawer
          applicationId={id}
          notice={application.notice}
          currentSectionId={currentSectionId}
          visible={streamPilotVisible}
          onHide={() => setStreamPilotVisible(false)}
          formData={formData}
          jsonData={jsonData}
          handleChange={handleChange}
          setToast={setToast}
        />
      )}
    </div>
  );
};

function formatDate(timestamp) {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  return date.toLocaleDateString("pt-PT", {
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default InovApplication;
